import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { COLLECTIONS, userSchema, type User } from '@budgetmonkey/shared'
import { db, functions } from '@/lib/firebase'
import { converterFor, nowIso } from '@/lib/firestore'

const TIMESTAMP_FIELDS = ['createdAt', 'updatedAt'] as const
const userConverter = converterFor(userSchema, TIMESTAMP_FIELDS)

const userRef = (userId: string) =>
  doc(db, COLLECTIONS.users, userId).withConverter(userConverter)

export async function fetchUser(userId: string): Promise<User | null> {
  const snapshot = await getDoc(userRef(userId))
  return snapshot.exists() ? snapshot.data() : null
}

/**
 * Create the profile document if it's missing. The security rules require the
 * document ID to equal the caller's UID and the stored email to match the auth
 * token, so both are taken from the token rather than from any form input.
 */
export async function ensureUser(
  userId: string,
  email: string,
  displayName: string,
): Promise<User> {
  const existing = await fetchUser(userId)
  if (existing !== null) return existing

  const timestamp = nowIso()
  const created: User = {
    userId,
    email,
    displayName,
    // Email forwarding is provisioned deliberately, not at signup: the address
    // is only real once its MX records resolve, and showing one that silently
    // drops mail is worse than showing none.
    ingestEmailAddress: null,
    ingestToken: null,
    trustedSenders: [],
    // Google usually supplies a real name, which is the right default for the
    // packet header. When it doesn't, leave this blank rather than printing an
    // email address on a district form — setup will ask for it.
    cardholderName: displayName.includes('@') ? '' : displayName,
    department: '',
    noReceiptWarningThreshold: 10,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
  await setDoc(userRef(userId), created)
  return created
}

export type UserProfileUpdate = Partial<
  Pick<
    User,
    'cardholderName' | 'department' | 'noReceiptWarningThreshold' | 'trustedSenders'
  >
>

/**
 * Ask the server for a forwarding address, or return the existing one.
 *
 * Server-issued rather than generated here: the routing token has to be unique
 * across users, which the browser can't check, and a client that picks its own
 * could choose one already in use and quietly receive someone else's receipts.
 */
export async function provisionIngestAddress(): Promise<{
  address: string
  created: boolean
}> {
  const call = httpsCallable<
    Record<string, never>,
    { address: string; created: boolean }
  >(functions, 'provisionIngestAddress')
  const result = await call({})
  return result.data
}

export async function updateUserProfile(
  userId: string,
  patch: UserProfileUpdate,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.users, userId), {
    ...patch,
    updatedAt: new Date(),
  })
}
